import { store } from 'app/shared/store/store';
import { nbnProductSearchApiService } from 'app/nbn/services/nbn-product-search-api/nbn-product-search-api.service';
import { ProductCatalogueProduct } from 'app/shared/models/product-catalogue-product.model';
import { QueryStringParamName } from 'app/shared/models/common.enums';
import { SelectedProductSessionData } from 'app/shared/models/sessionData';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { accountContractService } from 'app/energy/services/account-contracts.service';
import { customerProfileService } from 'app/shared/services/customer-profile/customerProfile-service';

class ChangePlanService {
  public isChangePlan(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const isChangePlan = urlParams.get(QueryStringParamName.IsChangePlan);
    const currentPlanSku = this.getCurrentPlanSku();

    if (isChangePlan && isChangePlan.toLowerCase().startsWith('true') && currentPlanSku?.length > 0) {
      return true;
    }

    return false;
  }

  public getCurrentPlanSku(): string {
    const customerProfile = customerProfileService.getCustomerProfile();
    return customerProfile.existingNbnProductId;
  }

  public async getCurrentPlan(): Promise<ProductCatalogueProduct> {
    const currentPlanSku = this.getCurrentPlanSku();
    if (currentPlanSku?.length > 0) return await nbnProductSearchApiService.getNbnProduct(currentPlanSku);
    return null;
  }

  public async hasEnergyContract(): Promise<boolean> {
    const customerProfile = customerProfileService.getCustomerProfile();
    if (customerProfile != null && customerProfile.hasExistingEnergy !== undefined) {
      return customerProfile.hasExistingEnergy;
    }

    let contractData = store.get('contractsData');
    if (contractData == null || contractData.customerData?.contractsResponse?.contracts?.length == 0) {
      const contractResult = await accountContractService.getEnergyContract();
      if (contractResult) {
        store.set('contractsData', contractResult);
      }
    }

    contractData = store.get('contractsData');
    if (contractData) {
      const hasExistingEnergy = contractData.customerData?.contractsResponse?.contracts?.length > 0;
      if (customerProfile !== null) {
        // update the customer profile in the session storage key if customer profile already in session storage.
        customerProfile.hasExistingEnergy = hasExistingEnergy;
        customerProfileService.setCustomerProfile(customerProfile);
      }
      return hasExistingEnergy;
    }
    return false;
  }

  public getLocationIdFromSelectedProduct(): string {
    const selectedProduct: SelectedProductSessionData = store.get(StoreKeys.selectedNbnProductDetails);

    if (selectedProduct) {
      if (Array.isArray(selectedProduct.productDetails) && selectedProduct.productDetails.length == 1)
        return selectedProduct.productDetails[0]?.serviceQualification?.nbn?.nbnLocation[0]?.locationId;

      return (selectedProduct.productDetails as any)?.serviceQualification?.nbn?.nbnLocation[0]?.locationId;
    }

    return null;
  }

  public getSelectedProductSku(): string | null {
    const selectedProduct: SelectedProductSessionData | null = store.get(StoreKeys.selectedNbnProductDetails);
    if (selectedProduct) {
      if (Array.isArray(selectedProduct.productDetails)) return selectedProduct.productDetails[0]?.product?.sku;
      return (selectedProduct.productDetails as any)?.product?.sku;
    }
    return null;
  }

  public clearSelectedProduct(): void {
    store.set(StoreKeys.selectedNbnProductDetails, null);
  }

  public getChangePlanProductsForDisplay(
    sqProducts: ProductCatalogueProduct[],
    filteredProducts: ProductCatalogueProduct[]
  ): ProductCatalogueProduct[] {
    // get the current plan product first
    let displayedProducts: ProductCatalogueProduct[] = [];
    const currentPlanSku = this.getCurrentPlanSku();

    // https://aglenergy.atlassian.net/browse/TEC-26252: changes to remove the current plan
    // from IPP page as per design due to incorrect pricing. Commented following code
    // until future notice.
    // const currentPlanProduct = sqProducts.find((p) => p.sku === currentPlanSku);
    // if (currentPlanProduct) {
    //  displayedProducts.push(currentPlanProduct);
    // }

    const selectedProductSku = this.getSelectedProductSku();
    if (selectedProductSku && selectedProductSku.length > 0 && selectedProductSku !== currentPlanSku) {
      displayedProducts.push(sqProducts.find((p) => p.sku === selectedProductSku));
    } else {
      displayedProducts = [...displayedProducts, ...filteredProducts.filter((p) => p.sku !== currentPlanSku)];
    }

    return displayedProducts;
  }
}

export const changePlanService = new ChangePlanService();
